import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authedFetch } from '@/lib/auth';
import { useLanguage } from '@/lib/i18n';

const API = process.env.REACT_APP_BACKEND_URL;

/* SystemHealthBadge — always-visible operator health indicator in the admin top
   bar. One cheap poll (/api/admin/ops/status) every 60s → a colored dot + short
   label, linking to the Command Center. Lets an operator see at a glance,
   from ANY admin page, whether the system needs attention. */
const TONE = {
  good: { dot: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  warn: { dot: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', bg: 'bg-amber-50' },
  bad: { dot: 'bg-rose-500', ring: 'ring-rose-200', text: 'text-rose-700', bg: 'bg-rose-50' },
  idle: { dot: 'bg-slate-300', ring: 'ring-slate-200', text: 'text-slate-500', bg: 'bg-slate-50' },
};

export function SystemHealthBadge() {
  const { lang } = useLanguage();
  const L = (en, ja) => (lang === 'ja' ? ja : en);
  const [s, setS] = useState(null);
  const [overall, setOverall] = useState('idle');
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await authedFetch(`${API}/api/admin/ops/status`, { credentials: 'include' });
      if (!res.ok) { setOverall('idle'); return; }
      const d = await res.json();
      setS(d);
      setOverall(d.overall || 'idle');
    } catch { setOverall('idle'); }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, [load]);

  const t = TONE[overall] || TONE.idle;
  const issues = s?.issues?.total ?? 0;
  const failed = s?.today?.failed ?? 0;
  const label = overall === 'good'
    ? L('All systems normal', 'すべて正常')
    : overall === 'bad'
      ? (s?.today?.stale ? L('Scheduler stalled', 'スケジューラ停止')
         : s?.scheduler?.effective === 'disabled' ? L('Scheduler off', 'スケジューラ無効')
         : failed > 0 ? L(`${failed} failed today`, `本日 ${failed}件失敗`) : L('Needs attention', '要対応'))
      : overall === 'warn'
        ? (issues > 0 ? L(`${issues} open issue${issues > 1 ? 's' : ''}`, `未解決 ${issues}件`)
           : s?.scheduler?.effective === 'paused' ? L('Scheduler paused', 'スケジューラ一時停止') : L('Minor issues', '軽微な問題'))
        : L('Status', 'ステータス');

  return (
    <Link
      to="/admin"
      data-testid="system-health-badge"
      data-status={overall}
      title={L('System status — open Command Center', 'システム状態 — コマンドセンターを開く')}
      className={`hidden sm:inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${t.bg} ${t.text} ${t.ring} hover:brightness-95 transition`}
    >
      <span className={`relative flex h-2 w-2`}>
        {overall !== 'good' && overall !== 'idle' && (
          <span className={`absolute inline-flex h-full w-full rounded-full ${t.dot} opacity-60 animate-ping`} />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${t.dot}`} />
      </span>
      {label}
    </Link>
  );
}
